import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, SearchIcon, ChevronRightIcon, LockIcon, ShieldCheckIcon, CheckIcon, HeartHandshakeIcon, ReceiptIcon, BuildingIcon, LandmarkIcon, FileTextIcon, UsersIcon, MapPinIcon, NavigationIcon, HandHeartIcon, DownloadIcon, Share2Icon, StoreIcon, ClockIcon, CalendarClockIcon, PlusIcon, XIcon, Edit3Icon, Trash2Icon, AlertTriangleIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { calculateScheduleTotals } from '../utils/schedule';
import { HomeIconButton, TransactionReceipt } from './TransactionReceipt';
// ── Shared CTA button style (canonical, matches SendMoneyScreen) ────────────
// height: 55px | width: 280px | border-radius: 8px | color: #F37024
const ctaStyle: React.CSSProperties = {
  height: '55px',
  width: '280px',
  borderRadius: '8px',
  background: '#F37024',
  color: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '16px',
  border: 'none',
  cursor: 'pointer',
  transition: 'opacity 0.15s',
  margin: '0 auto'
};
interface ZakaatScreenProps {
  onBack?: () => void;
  onNavigateHome?: () => void;
  onNavigateToMerchantRequests?: () => void;
  onNavigateToMerchantHistory?: () => void;
}
type Step = 'services' | 'orgs' | 'review' | 'otp' | 'success' | 'merchant_menu' | 'scheduled_list' | 'scheduled_new' | 'scheduled_review' | 'scheduled_mpin' | 'scheduled_success' | 'scheduled_detail';
type OrgTab = 'zakat' | 'sadqaat';
interface Org {
  id: string;
  name: string;
  account: string;
  initials: string;
  color: string;
}
const MORE_SERVICES = [{
  id: 'zakat',
  label: 'Zakat / Donations',
  desc: 'Pay Zakat, Fitra and Sadqaat securely',
  icon: HandHeartIcon,
  highlight: true
}, {
  id: 'merchant',
  label: 'Merchant Payment',
  desc: 'Payment requests and P2M history',
  icon: StoreIcon,
  highlight: false
}, {
  id: 'scheduled',
  label: 'Scheduled Payments',
  desc: 'Manage recurring and future payments',
  icon: CalendarClockIcon,
  highlight: false
}];
export interface ScheduledPayment {
  id: string;
  payTo: string;
  payToAccount: string;
  payFrom: string;
  payFromAccount: string;
  amount: number;
  type: string;
  frequency: string;
  startDate: string;
  endDate?: string;
  time: string;
  status: 'Active' | 'Completed' | 'Failed';
  createdOn: string;
  comments?: string;
}
const MOCK_SCHEDULED: ScheduledPayment[] = [{
  id: 'sp1',
  payTo: 'Al-khidmat Foundation',
  payToAccount: '1234 **** **** 6544',
  payFrom: 'Kashish Shewani',
  payFromAccount: '1234 **** **** 6544',
  amount: 5000,
  type: 'Zakat',
  frequency: 'Monthly',
  startDate: '20/12/2026',
  endDate: '20/12/2027',
  time: '09:45 AM',
  status: 'Active',
  createdOn: '15/11/2026'
}, {
  id: 'sp2',
  payTo: 'Storm Fiber',
  payToAccount: '03223556978',
  payFrom: 'Kashish Shewani',
  payFromAccount: '1234 **** **** 6544',
  amount: 3448,
  type: 'Internet Bill',
  frequency: 'Once',
  startDate: '25/12/2026',
  time: '12:00 PM',
  status: 'Failed',
  createdOn: '10/12/2026'
}];
const ZAKAT_ORGS: Org[] = [{
  id: 'edhi',
  name: 'Edhi Foundation',
  account: 'PK24BOPM0001234567',
  initials: 'EF',
  color: '#1E7A46'
}, {
  id: 'skmt',
  name: 'Shaukat Khanum Memorial',
  account: 'PK11BOPM0009988776',
  initials: 'SK',
  color: '#C0392B'
}, {
  id: 'saylani',
  name: 'Saylani Welfare Trust',
  account: 'PK56BOPM0004455667',
  initials: 'SW',
  color: '#2E86C1'
}, {
  id: 'alkhidmat',
  name: 'Al Khidmat Foundation',
  account: 'PK78BOPM0007788991',
  initials: 'AK',
  color: '#117864'
}, {
  id: 'akhuwat',
  name: 'Akhuwat Foundation',
  account: 'PK33BOPM0002211009',
  initials: 'AF',
  color: '#7D3C98'
}];
const SADQAAT_ORGS: Org[] = [{
  id: 'jdc',
  name: 'JDC Foundation Pakistan',
  account: 'PK90BOPM0006677889',
  initials: 'JD',
  color: '#D68910'
}, {
  id: 'chhipa',
  name: 'Chhipa Welfare Association',
  account: 'PK45BOPM0003344556',
  initials: 'CW',
  color: '#CB4335'
}, {
  id: 'indus',
  name: 'Indus Hospital & Health Network',
  account: 'PK67BOPM0005566778',
  initials: 'IH',
  color: '#1F618D'
}, {
  id: 'transparent',
  name: 'Transparent Hands',
  account: 'PK12BOPM0008899001',
  initials: 'TH',
  color: '#148F77'
}];
const CUSTOMER = {
  name: 'KASHISH SOHAIL SHEWANI',
  account: '1234-5678-9012',
  balance: 250000
};
const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (dir: number) => ({
    x: dir < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
const formatPKR = (n: number) => n.toLocaleString('en-US', {
  minimumFractionDigits: 0
});
export function ZakaatScreen({
  onBack,
  onNavigateHome,
  onNavigateToMerchantRequests,
  onNavigateToMerchantHistory
}: ZakaatScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [step, setStep] = useState<Step>('services');
  const [direction, setDirection] = useState(1);
  const [tab, setTab] = useState<OrgTab>('zakat');
  const [search, setSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
  const [amount, setAmount] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(15);
  // Scheduled Payments State
  const [scheduledPayments, setScheduledPayments] = useState<ScheduledPayment[]>(MOCK_SCHEDULED);
  const [schedSearch, setSchedSearch] = useState('');
  const [schedTab, setSchedTab] = useState<'once' | 'recurring'>('once');
  const [schedDate, setSchedDate] = useState('');
  const [schedEndDate, setSchedEndDate] = useState('');
  const [schedTime, setSchedTime] = useState('12:00 AM');
  const [schedTerms, setSchedTerms] = useState(false);
  const [schedAmount, setSchedAmount] = useState('');
  const [schedPayTo, setSchedPayTo] = useState('');
  const [schedPayFrom, setSchedPayFrom] = useState('Kashish Shewani / 1234 **** **** 6544');
  const [schedType, setSchedType] = useState('');
  const [schedFreq, setSchedFreq] = useState('Monthly');
  const [schedCustomDays, setSchedCustomDays] = useState('');
  const [schedComments, setSchedComments] = useState('');
  const [selectedSched, setSelectedSched] = useState<ScheduledPayment | null>(null);
  const [schedMpin, setSchedMpin] = useState(['', '', '', '']);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isEditingSched, setIsEditingSched] = useState(false);
  const navigateTo = (next: Step, dir = 1) => {
    setDirection(dir);
    setStep(next);
  };
  // ---- Theme palette ----
  const isLight = !isDark && !isPlatinum;
  const textPri = isLight ? 'text-[#1A1A1A]' : 'text-white';
  const textSec = isLight ? 'text-[#6B6B6B]' : isPlatinum ? 'text-[#A0A0A5]' : 'text-[#B8A89E]';
  const pageBg = isLight ? 'bg-[#FFF5F0]' : isPlatinum ? 'bg-[#1C1C1E]' : 'bg-[#1A0E08]';
  const cardSurface = isLight ? 'bg-white border border-[#F0E8E3]' : isPlatinum ? 'bg-[#2C2C2E] border border-[rgba(160,160,165,0.12)]' : 'bg-[#2D1810] border border-[rgba(243,112,36,0.12)]';
  const maroon = isLight ? '#5A1408' : '#F37024';
  const donationType = tab === 'zakat' ? 'Zakat / Fitra' : 'Sadqaat';
  // OTP resend timer
  useEffect(() => {
    if (step !== 'otp') return;
    setResendTimer(15);
    setOtp(['', '', '', '', '', '']);
  }, [step]);
  useEffect(() => {
    if (step !== 'otp' || resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(id);
  }, [step, resendTimer]);
  // Amount validation
  const amountNum = Number(amount);
  const amountValid = amount !== '' && amountNum > 0 && amountNum <= CUSTOMER.balance;
  const amountError = amount !== '' && (amountNum <= 0 ? 'Enter an amount greater than zero' : amountNum > CUSTOMER.balance ? 'Amount exceeds available balance' : '');
  const otpComplete = otp.every(d => d !== '');
  const handleOtpChange = (index: number, value: string) => {
    // Paste support
    if (value.length > 1) {
      const digits = value.replace(/[^\d]/g, '').slice(0, 6).split('');
      const next = ['', '', '', '', '', ''];
      digits.forEach((d, i) => next[i] = d);
      setOtp(next);
      const lastIdx = Math.min(digits.length, 6) - 1;
      if (lastIdx >= 0) document.getElementById(`zakat-otp-${Math.min(lastIdx + 1, 5)}`)?.focus();
      return;
    }
    const digit = value.replace(/[^\d]/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) {
      document.getElementById(`zakat-otp-${index + 1}`)?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`zakat-otp-${index - 1}`)?.focus();
    }
  };
  const txnId = 'TXN20260012345';
  const refNumber = 'DON000987654';
  const now = new Date();
  const txnDateTime = now.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  // Reusable header
  const Header = ({
    title,
    onBackClick
  }: {
    title: string;
    onBackClick: () => void;
  }) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-3768" data-mp="3pzap6hfeus8t">
      <button onClick={onBackClick} aria-label="Back" className={`p-2 -ml-2 ${textPri}`} data-id="element-3769" data-mp="3pyis12660mgf">
        <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-3770" data-mp="1uq2hliwnoag1" />
      </button>
      <h1 className={`text-[18px] font-semibold ${textPri}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3771" data-mp="1zo3zw1m9c5qg">
        {title}
      </h1>
      <HomeIconButton onNavigateHome={onNavigateHome} data-id="element-3772" data-mp="1pp51dbv7hqh6" />
    </div>;
  // ===== Screen 1: More Services =====
  const renderServices = () => <motion.div key="services" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-10 ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3773" data-mp="2idalbwefrbfy">
      <Header title="More Services" onBackClick={onBack ?? (() => {})} data-id="element-3774" data-mp="2wd39pl2hpfkr" />
      <div className="px-6 mt-2" data-id="element-3775" data-mp="2wbs73oks6dv8">
        <p className={`text-[13px] mb-5 ${textSec}`} data-id="element-3776" data-mp="313job8i1r78p">
          Additional banking services at your fingertips.
        </p>
        <div className="space-y-3" data-id="element-3777" data-mp="po1cv1yu8y84">
          {MORE_SERVICES.map((s, idx) => {
          const Icon = s.icon;
          const isZakat = s.id === 'zakat';
          return <motion.button key={s.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: idx * 0.04
          }} whileTap={{
            scale: 0.98
          }} onClick={() => {
            if (isZakat) navigateTo('orgs');else if (s.id === 'merchant') navigateTo('merchant_menu');else if (s.id === 'scheduled') navigateTo('scheduled_list');
          }} className={`w-full p-4 rounded-lg flex items-center gap-4 text-left transition-colors ${isZakat ? isLight ? 'bg-[#F37024]/8 border-2 border-[#F37024]' : 'bg-[#F37024]/12 border-2 border-[#F37024]' : cardSurface}`} data-id="element-3778" data-mp="zf5nxqle7yxo" data-mpi={idx}>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{
              background: isZakat ? '#F37024' : isLight ? '#FBE4D3' : 'rgba(243,112,36,0.14)'
            }} data-id="element-3779" data-mp="1z8kssje4flqf">
                  <Icon className="w-5 h-5" strokeWidth={2} style={{
                color: isZakat ? '#FFFFFF' : '#F37024'
              }} data-id="element-3780" data-mp="1ggs1ctepa8kt" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-3781" data-mp="sqnvsghybyr6">
                  <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-3782" data-mp="spwb40pw7lfo">
                    {s.label}
                  </p>
                  <p className={`text-[12px] truncate ${textSec}`} data-id="element-3783" data-mp="3b01zzcpm23xw">{s.desc}</p>
                </div>
                <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3784" data-mp="36xx8ajiq3hdw" />
              </motion.button>;
        })}
        </div>
      </div>
    </motion.div>;
  // ===== Screen 2: Org List =====
  const orgs = tab === 'zakat' ? ZAKAT_ORGS : SADQAAT_ORGS;
  const filteredOrgs = orgs.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));
  const renderOrgs = () => <motion.div key="orgs" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-10 ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3785" data-mp="2j8m5yxlq5exn">
      <Header title="Zakat / Fitra & Sadqaat" onBackClick={() => navigateTo('services', -1)} data-id="element-3786" data-mp="24bbtbkxci30c" />

      {/* Tabs */}
      <div className="px-6 mt-2" data-id="element-3787" data-mp="898fohimynbq">
        <div className={`relative flex rounded-lg p-1 ${isLight ? 'bg-[#F5EBE6]' : 'bg-[#2D1810]'}`} data-id="element-3788" data-mp="88yy9ibnpohn">
          <motion.div layout className="absolute top-1 bottom-1 rounded-lg bg-[#F37024] shadow-md" initial={false} animate={{
          left: tab === 'zakat' ? 4 : '50%',
          right: tab === 'zakat' ? '50%' : 4
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} data-id="element-3789" data-mp="d94nysyd90x3" />
          <button onClick={() => {
          setTab('zakat');
          setSearch('');
        }} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-lg transition-colors ${tab === 'zakat' ? 'text-white' : textSec}`} data-id="element-3790" data-mp="3bpfmgwwcl0oq">
            Zakat / Fitra
          </button>
          <button onClick={() => {
          setTab('sadqaat');
          setSearch('');
        }} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-lg transition-colors ${tab === 'sadqaat' ? 'text-white' : textSec}`} data-id="element-3791" data-mp="1evcb9zosxqmb">
            Sadqaat
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-6 mt-5" data-id="element-3792" data-mp="34dhlpwxzi89l">
        <div className={`flex items-center gap-3 px-4 rounded-lg h-[52px] ${cardSurface}`} data-id="element-3793" data-mp="34cgbfuoslpsx">
          <SearchIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3794" data-mp="3i78yrfaxcaa2" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search charitable organization" className={`w-full bg-transparent outline-none text-[14px] ${textPri} placeholder:opacity-50`} data-id="element-3795" data-mp="3s3byyfm7tyud" />
        </div>
      </div>

      {/* Org list */}
      <div className="px-6 mt-5 space-y-3" data-id="element-3796" data-mp="1sqrclpwfbrzt">
        {filteredOrgs.length === 0 && <p className={`text-center text-[13px] mt-8 ${textSec}`} data-id="element-3797" data-mp="3ssqvtnqhpjc0">
            No organizations found.
          </p>}
        {filteredOrgs.map((org, idx) => <motion.button key={org.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        delay: idx * 0.04
      }} whileTap={{
        scale: 0.98
      }} onClick={() => {
        setSelectedOrg(org);
        setAmount('');
        navigateTo('review');
      }} className={`w-full p-4 rounded-lg flex items-center gap-4 text-left ${cardSurface}`} data-id="element-3798" data-mp="1oadr0d6y0m4" data-mpi={idx}>
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-white text-[14px] font-bold" style={{
          background: org.color
        }} data-id="element-3799" data-mp="ycyrvllsd3q9">
              {org.initials}
            </div>
            <div className="min-w-0 flex-1" data-id="element-3800" data-mp="18gdklc4z67vz">
              <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-3801" data-mp="18fm0oykqpylj">
                {org.name}
              </p>
              <p className={`text-[12px] truncate ${textSec}`} data-id="element-3802" data-mp="13hkdq2k71f4s">{org.account}</p>
            </div>
            <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3803" data-mp="zhicfuidenq1" />
          </motion.button>)}
      </div>
    </motion.div>;
  // ===== Screen 3: Review =====
  const serviceCharge = 0;
  const totalAmount = amountNum + serviceCharge;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3804" data-mp="1ietaxcw6hd4f">
      <Header title="Review" onBackClick={() => navigateTo('orgs', -1)} data-id="element-3805" data-mp="2tt851ujbose7" />
      <div className="px-6 mt-2 flex-1 overflow-y-auto" data-id="element-3806" data-mp="2trx0hsi1zgm1">
        {/* Customer account */}
        <div className={`rounded-lg p-5 mb-4 ${cardSurface}`} data-id="element-3807" data-mp="2ymkpdsgyyd15">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3808" data-mp="2jwwp8ukixkf0">
            From Account
          </p>
          <p className={`text-[15px] font-semibold ${textPri}`} data-id="element-3809" data-mp="3clamv97paeq1">
            {CUSTOMER.name}
          </p>
          <p className={`text-[13px] mt-1 ${textSec}`} data-id="element-3810" data-mp="3hi1788qjfwh9">{CUSTOMER.account}</p>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5" data-id="element-3811" data-mp="32muvd54rh3g0">
            <span className={`text-[12px] ${textSec}`} data-id="element-3812" data-mp="37kda5c5gmcvx">Available Balance</span>
            <span className={`text-[14px] font-bold ${textPri}`} data-id="element-3813" data-mp="1r76acjb2iehu">
              PKR {formatPKR(CUSTOMER.balance)}
            </span>
          </div>
        </div>

        {/* Beneficiary */}
        <div className={`rounded-lg p-5 mb-4 ${cardSurface}`} data-id="element-3814" data-mp="e8e4rttgi39f">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3815" data-mp="e6te4fa9j4gu">
            Beneficiary
          </p>
          <div className="space-y-3" data-id="element-3816" data-mp="n8j3lfkl3tbz">
            <div className="flex justify-between gap-3" data-id="element-3817" data-mp="j7hbdip34hji">
              <span className={`text-[13px] ${textSec}`} data-id="element-3818" data-mp="j7z6ud3xarqd">Organization</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3819" data-mp="3roc5o9rtgtlz">
                {selectedOrg?.name}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3820" data-mp="4aq7aw4guhnk">
              <span className={`text-[13px] ${textSec}`} data-id="element-3821" data-mp="4cb2mnylu4mh">Account No.</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3822" data-mp="3vnb8notzmgas">
                {selectedOrg?.account}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3823" data-mp="33wwzu70tzu5z">
              <span className={`text-[13px] ${textSec}`} data-id="element-3824" data-mp="1xz943bo48cq9">Donation Type</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3825" data-mp="1xx64q112p78v">
                {donationType}
              </span>
            </div>
          </div>
        </div>

        {/* Amount input */}
        <div className="mb-4" data-id="element-3826" data-mp="22ydbbwrhc0dy">
          <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3827" data-mp="22x26ruq7mols">
            Amount (PKR)
          </label>
          <input type="text" inputMode="numeric" value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} placeholder="Enter amount" className={`w-full h-[54px] px-4 rounded-lg text-[18px] font-semibold outline-none border-2 transition-colors ${textPri} ${amountError ? 'border-[#EF4444]' : 'border-transparent focus:border-[#F37024]'} ${cardSurface}`} data-id="element-3828" data-mp="2hpchz28r8euw" />
          {amountError && <p className="text-[12px] text-[#EF4444] mt-1.5 ml-1" data-id="element-3829" data-mp="rd6mdrep4fb8">
              {amountError}
            </p>}
        </div>

        {/* Transaction summary */}
        <div className={`rounded-lg p-5 mb-4 ${cardSurface}`} data-id="element-3830" data-mp="37w7agegvuw11">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3831" data-mp="2u6na1faryauw">
            Transaction Summary
          </p>
          <div className="space-y-3" data-id="element-3832" data-mp="2k7ej6vb7hccr">
            <div className="flex justify-between" data-id="element-3833" data-mp="2k6cxzy0h79hb">
              <span className={`text-[13px] ${textSec}`} data-id="element-3834" data-mp="2p6r2x76s63b4">Donation Amount</span>
              <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3835" data-mp="2p4o90c2ibq14">
                PKR {amountNum > 0 ? formatPKR(amountNum) : '0'}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-3836" data-mp="2f94muhdqw5pk">
              <span className={`text-[13px] ${textSec}`} data-id="element-3837" data-mp="9fd4xoushsa5">Service Charges</span>
              <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3838" data-mp="dhhqs1ezxkm8">
                PKR {formatPKR(serviceCharge)}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t border-black/5" data-id="element-3839" data-mp="1mfzqde8tf5fs">
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-3840" data-mp="1heaczry4efoj">
                Total Amount
              </span>
              <span className={`text-[14px] font-bold ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3841" data-mp="1de9zc9iui8ij">
                PKR {amountNum > 0 ? formatPKR(totalAmount) : '0'}
              </span>
            </div>
          </div>
        </div>

        {/* Security indicator */}
        <div className={`flex items-center gap-2.5 px-4 py-3 rounded-lg ${isLight ? 'bg-[#10B981]/8' : 'bg-[#10B981]/12'}`} data-id="element-3842" data-mp="2144c6r35zx2s">
          <ShieldCheckIcon className="w-5 h-5 text-[#10B981] shrink-0" strokeWidth={2} data-id="element-3843" data-mp="1rdrlsi8o59oq" />
          <span className={`text-[12px] font-medium ${textPri}`} data-id="element-3844" data-mp="2otynv5bqzflg">
            Secure Banking Transaction
          </span>
          <LockIcon className="w-4 h-4 text-[#10B981] ml-auto shrink-0" strokeWidth={2} data-id="element-3845" data-mp="2k0m3j7e3puyi" />
        </div>
      </div>

      <div className="px-6 mt-auto pt-6 flex justify-center" data-id="element-3846" data-mp="2wc685q6ekr9s">
        <button onClick={() => navigateTo('otp')} disabled={!amountValid} style={{
        ...ctaStyle,
        opacity: !amountValid ? 0.5 : 1,
        cursor: !amountValid ? 'not-allowed' : 'pointer'
      }} className="active:scale-[0.98] transition-transform shadow-md" data-id="element-3847" data-mp="2weipq2890zmb">
          Pay Now
        </button>
      </div>
    </motion.div>;
  // ===== Screen 4: OTP =====
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3848" data-mp="3k31z6l31bifz">
      <Header title="Verification" onBackClick={() => navigateTo('review', -1)} data-id="element-3849" data-mp="1x5qg89eox1n" />
      <div className="px-4 sm:px-6 mt-4 flex-1 max-w-md mx-auto w-full" data-id="element-3850" data-mp="3kdqd54uua52q">
        <h2 className={`text-[20px] font-light mb-2 ${textPri}`} data-id="element-3851" data-mp="3ge9a0i2vxk0q">Enter OTP</h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-3852" data-mp="3u4l4gnmxzuv1">
          A 6-digit OTP has been sent to your registered mobile number.
        </p>

        <div className="grid grid-cols-6 gap-2 sm:gap-3 max-w-[340px] mx-auto mb-8" data-id="element-3853" data-mp="3p72s0n9puxia">
          {otp.map((digit, index) => <input key={index} id={`zakat-otp-${index}`} type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={index === 0 ? 6 : 1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className={`
                h-12 sm:h-14
                w-full
                rounded-xl
                text-center
                text-lg sm:text-xl
                font-bold
                outline-none
                border-2
                transition-colors
                ${digit ? 'border-[#F37024] bg-[#F37024]/10' : cardSurface}
                ${textPri}
              `} data-id="element-3854" data-mp="31i9redi5g1hc" data-mpi={index} />)}
        </div>

        <div className="flex items-center justify-center gap-1.5" data-id="element-3855" data-mp="1zkkjuf85elhf">
          <span className={`text-[13px] ${textSec}`} data-id="element-3856" data-mp="1zmxcpnfg2g99">
            Didn't receive the OTP?
          </span>
          {resendTimer > 0 ? <span className="text-[13px] font-semibold" style={{
          color: maroon,
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-3857" data-mp="2rci8xnmynio0">
              Resend in 00:{String(resendTimer).padStart(2, '0')}
            </span> : <button onClick={() => setResendTimer(15)} className="text-[13px] font-bold text-[#F37024] hover:underline" data-id="element-3858" data-mp="368hrxtblz37x">
              Resend
            </button>}
        </div>
      </div>

      <div className="px-6 mt-auto pt-6 flex justify-center" data-id="element-3859" data-mp="3ku5chflq49rb">
        <button onClick={() => navigateTo('success')} disabled={!otpComplete} style={{
        ...ctaStyle,
        opacity: !otpComplete ? 0.5 : 1,
        cursor: !otpComplete ? 'not-allowed' : 'pointer'
      }} className="active:scale-[0.98] transition-transform shadow-md" data-id="element-3860" data-mp="3fy6jfepga3kk">
          Submit
        </button>
      </div>
    </motion.div>;
  const handleDownloadReceipt = () => {
    window.print();
  };
  const handleShareReceipt = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Donation Receipt',
          text: `Donation of PKR ${amountNum} successfully sent to ${selectedOrg?.name}`
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
  // ===== Screen 5: Success (canonical receipt) =====
  const renderSuccess = () => <TransactionReceipt amount={amountNum > 0 ? amountNum : 5000} refNumber={refNumber} toName={selectedOrg?.name ?? ''} toAccount={selectedOrg?.account} fromName={CUSTOMER.name} fromAccount={CUSTOMER.account} additionalInfo={[{
    label: 'Donation Type',
    value: donationType
  }, {
    label: 'Transaction ID',
    value: txnId
  }, {
    label: 'Reference No.',
    value: refNumber
  }]} onNavigateHome={onNavigateHome} onShare={handleShareReceipt} onSaveImage={handleDownloadReceipt} data-id="element-3861" data-mp="tqa4qqi6ckea" />;
  // ===== Scheduled Payments: List =====
  const renderScheduledList = () => {
    const filtered = scheduledPayments.filter(p => p.payTo.toLowerCase().includes(schedSearch.toLowerCase()) || p.type.toLowerCase().includes(schedSearch.toLowerCase()));
    const active = filtered.filter(p => p.status === 'Active');
    const past = filtered.filter(p => p.status !== 'Active');
    return <motion.div key="scheduled_list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3862" data-mp="2gtbr9ochorpy">
        <Header title="My Scheduled Payments" onBackClick={() => navigateTo('services', -1)} data-id="element-3863" data-mp="34q93ridy91e7" />

        <div className="px-6 mt-2 flex-1 overflow-y-auto" data-id="element-3864" data-mp="2zroak2x47hz9">
          <div className={`flex items-center gap-3 px-4 rounded-2xl h-[52px] mb-6 ${cardSurface}`} data-id="element-3865" data-mp="3djugyxaguali">
            <SearchIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3866" data-mp="39jainqe5p0xs" />
            <input value={schedSearch} onChange={e => setSchedSearch(e.target.value)} placeholder="Search scheduled payments" className={`w-full bg-transparent outline-none text-[14px] ${textPri} placeholder:opacity-50`} data-id="element-3867" data-mp="3nfo2h665xeff" />
          </div>

          {active.length > 0 && <div className="mb-6" data-id="element-3868" data-mp="3s6t6untocwy4">
              <h3 className={`text-[13px] font-bold uppercase tracking-wider mb-3 ${textSec}`} data-id="element-3869" data-mp="3s5rtudt3m0dr">
                Active Schedules
              </h3>
              <div className="space-y-3" data-id="element-3870" data-mp="3jcybu8m9omw7">
                {active.map(p => <div key={p.id} className={`p-4 rounded-2xl ${cardSurface}`} data-id="element-3871" data-mp="3o5807k7qhewd">
                    <div className="flex justify-between items-start mb-2" data-id="element-3872" data-mp="39ftk7t9njz80">
                      <div data-id="element-3873" data-mp="39dpbmy0t6c1e">
                        <p className={`text-[15px] font-bold ${textPri}`} data-id="element-3874" data-mp="3een01scew2bc">
                          {p.payTo}
                        </p>
                        <p className={`text-[12px] ${textSec}`} data-id="element-3875" data-mp="30gex93sp26s9">
                          {p.type} • {p.frequency}
                        </p>
                      </div>
                      <p className={`text-[15px] font-bold ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3876" data-mp="1rf7ki96rejcq">
                        PKR {formatPKR(p.amount)}
                      </p>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-3 border-t border-black/5 dark:border-white/5" data-id="element-3877" data-mp="21ey9xwggq85y">
                      <p className={`text-[11px] ${textSec}`} data-id="element-3878" data-mp="2adtgxarbzmab">
                        Next: {p.startDate} at {p.time}
                      </p>
                      <button onClick={() => {
                  setSelectedSched(p);
                  navigateTo('scheduled_detail');
                }} className={`text-[12px] font-bold px-4 py-1.5 rounded-full border ${isLight ? 'border-[#5A1408] text-[#5A1408]' : 'border-[#F37024] text-[#F37024]'}`} data-id="element-3879" data-mp="2fb22llbou8gb">
                        Review
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {past.length > 0 && <div data-id="element-3880" data-mp="icw2rvmqfggg">
              <h3 className={`text-[13px] font-bold uppercase tracking-wider mb-3 ${textSec}`} data-id="element-3881" data-mp="igjzf3nh4ize">
                Past / Failed
              </h3>
              <div className="space-y-3" data-id="element-3882" data-mp="8huf7483umfz">
                {past.map(p => <div key={p.id} className={`p-4 rounded-2xl opacity-75 ${cardSurface}`} data-id="element-3883" data-mp="162gogma9srtf">
                    <div className="flex justify-between items-start mb-2" data-id="element-3884" data-mp="wdyt4kkrfcx6">
                      <div data-id="element-3885" data-mp="wbvs75i2jzdo">
                        <p className={`text-[15px] font-bold ${textPri}`} data-id="element-3886" data-mp="rf5dali039tu">
                          {p.payTo}
                        </p>
                        <p className={`text-[12px] ${textSec}`} data-id="element-3887" data-mp="1ov1elvyl4itj">{p.type}</p>
                      </div>
                      <div className="text-right" data-id="element-3888" data-mp="1k2rh9uv54gh8">
                        <p className={`text-[15px] font-bold ${textPri}`} data-id="element-3889" data-mp="3ebdi0ct8se8a">
                          PKR {formatPKR(p.amount)}
                        </p>
                        <p className={`text-[10px] font-bold mt-0.5 ${p.status === 'Failed' ? 'text-[#EF4444]' : 'text-[#22C55E]'}`} data-id="element-3890" data-mp="3icgvwtrqc06v">
                          {p.status}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-3 border-t border-black/5 dark:border-white/5" data-id="element-3891" data-mp="2uluon4pjujxx">
                      <p className={`text-[11px] ${textSec}`} data-id="element-3892" data-mp="2zk4jtfb9xkjj">
                        Date: {p.startDate}
                      </p>
                      <button onClick={() => {
                  setSelectedSched(p);
                  navigateTo('scheduled_detail');
                }} className={`text-[12px] font-bold px-4 py-1.5 rounded-full border ${isLight ? 'border-[#5A1408] text-[#5A1408]' : 'border-[#F37024] text-[#F37024]'}`} data-id="element-3893" data-mp="2qlasa3csvkcu">
                        Review
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {filtered.length === 0 && <p className={`text-center text-[13px] mt-8 ${textSec}`} data-id="element-3894" data-mp="12ywthxak3rko">
              No scheduled payments found.
            </p>}
        </div>

        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] p-6 bg-gradient-to-t from-black/5 to-transparent flex justify-center" data-id="element-3895" data-mp="1cprj9bn0h9av">
          <button onClick={() => {
          setSchedTab('once');
          setSchedDate('');
          setSchedEndDate('');
          setSchedTime('12:00 AM');
          setSchedTerms(false);
          setSchedAmount('');
          setSchedPayTo('');
          setSchedType('');
          setSchedFreq('Monthly');
          setSchedCustomDays('');
          setSchedComments('');
          setIsEditingSched(false);
          navigateTo('scheduled_new');
        }} style={{
          ...ctaStyle,
          gap: '8px'
        }} className="shadow-lg active:scale-[0.98] transition-transform" data-id="element-3896" data-mp="1qp0mlaq4c8xf">
            <PlusIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3897" data-mp="7qch0fx89meb" />
            Schedule a New Payment
          </button>
        </div>
      </motion.div>;
  };
  // ===== Scheduled Payments: New/Edit =====
  const renderScheduledNew = () => {
    const isRecurring = schedTab === 'recurring';
    // Validation
    const amountNum = Number(schedAmount);
    const isValidAmount = schedAmount !== '' && amountNum > 0 && amountNum <= CUSTOMER.balance;
    const baseValid = isValidAmount && schedDate !== '' && schedTime !== '' && schedPayTo !== '' && schedType !== '' && schedTerms;
    // Total Scheduled Amount = Amount per Execution × Number of Scheduled Executions
    const {
      executions,
      totalAmount
    } = calculateScheduleTotals(amountNum || 0, isRecurring ? schedFreq : 'Once', schedDate, schedEndDate, schedCustomDays);
    const isValid = isRecurring ? baseValid && schedEndDate !== '' && executions > 0 : baseValid;
    const rangeInvalid = isRecurring && schedDate !== '' && schedEndDate !== '' && executions === 0;
    const durationMsg = isRecurring && executions > 0 ? `${executions} scheduled ${executions === 1 ? 'execution' : 'executions'}` : '';
    return <motion.div key="scheduled_new" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3898" data-mp="1s9yg16pg0anh">
        <Header title={isEditingSched ? 'Edit Schedule' : 'Schedule Payment'} onBackClick={() => navigateTo(isEditingSched ? 'scheduled_detail' : 'scheduled_list', -1)} data-id="element-3899" data-mp="1eb8hrnqw04xj" />

        {!isEditingSched && <div className="px-6 mt-2" data-id="element-3900" data-mp="upges5waztla">
            <div className={`relative flex rounded-2xl p-1 ${isLight ? 'bg-[#F5EBE6]' : 'bg-[#2D1810]'}`} data-id="element-3901" data-mp="zlx0ktg15vv7">
              <motion.div layout className="absolute top-1 bottom-1 rounded-xl bg-[#F37024] shadow-md" initial={false} animate={{
            left: schedTab === 'once' ? 4 : '50%',
            right: schedTab === 'once' ? '50%' : 4
          }} transition={{
            type: 'spring',
            damping: 28,
            stiffness: 280
          }} data-id="element-3902" data-mp="1ndcb39hynjhj" />
              <button onClick={() => setSchedTab('once')} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-xl transition-colors ${schedTab === 'once' ? 'text-white' : textSec}`} data-id="element-3903" data-mp="2b2dlv3e9n6pz">
                Set for Once
              </button>
              <button onClick={() => setSchedTab('recurring')} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-xl transition-colors ${schedTab === 'recurring' ? 'text-white' : textSec}`} data-id="element-3904" data-mp="8dn5rlzuw5jj">
                Recurring
              </button>
            </div>
          </div>}

        <div className="px-6 mt-6 flex-1 overflow-y-auto space-y-5" data-id="element-3905" data-mp="3lttdpd7nr6zd">
          {isRecurring && <div data-id="element-3906" data-mp="3guqdq76jbqy9">
              <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3907" data-mp="3gu8er2shmymn">
                Set Frequency
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-3908" data-mp="115f7pef9bj4n">
                {['Weekly', 'Monthly', 'Quarterly', 'Custom'].map(f => <button key={f} onClick={() => setSchedFreq(f)} className={`px-4 py-2 rounded-xl text-[12px] font-bold transition-colors ${schedFreq === f ? 'bg-[#F37024] text-white' : `${cardSurface} ${textSec}`}`} data-id="element-3909" data-mp="1g75ghxfc3u11">
                    {f}
                  </button>)}
              </div>
              {schedFreq === 'Custom' && <div className="mt-3" data-id="element-3910" data-mp="1ywllawy1y8n9">
                  <input type="text" value={schedCustomDays} onChange={e => setSchedCustomDays(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Enter No. of Days" className={`w-full h-[48px] px-4 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3911" data-mp="23w7yx7hslqwl" />
                  <div className="flex flex-wrap gap-2 mt-2" data-id="element-3912" data-mp="iky2xy11yrcj">
                    {['Daily', '15 Days', '60 Days', '180 Days', '365 Days'].map(d => <button key={d} onClick={() => setSchedCustomDays(d.split(' ')[0])} className={`px-3 py-1.5 rounded-lg text-[11px] font-medium ${cardSurface} ${textSec}`} data-id="element-3913" data-mp="1a2okq6sxpt6">
                        {d}
                      </button>)}
                  </div>
                </div>}
            </div>}

          <div className="grid grid-cols-2 gap-3" data-id="element-3914" data-mp="fypbbcyrdjuf">
            <div data-id="element-3915" data-mp="fxxe5xpije21">
              <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3916" data-mp="g0jkxv4kxpj7">
                {isRecurring ? 'Start Date' : 'Set Date'}
              </label>
              <input type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3917" data-mp="30qr5dvdu51wh" />
            </div>
            {isRecurring && <div data-id="element-3918" data-mp="2f4m6azcb5ded">
                <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3919" data-mp="2f55q6lliqbv1">
                  End Date
                </label>
                <input type="date" value={schedEndDate} onChange={e => setSchedEndDate(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3920" data-mp="25ejbqupjfrdx" />
              </div>}
            {!isRecurring && <div data-id="element-3921" data-mp="1mi1ddug6ot8p">
                <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3922" data-mp="1mhi4f38ihf6t">
                  Set Time
                </label>
                <input type="time" value={schedTime} onChange={e => setSchedTime(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3923" data-mp="1hal6bjqxuszp" />
              </div>}
          </div>

          {isRecurring && <div data-id="element-3924" data-mp="ylwml146ytpi">
              <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3925" data-mp="yjaixckf8yck">
                Set Time
              </label>
              <input type="time" value={schedTime} onChange={e => setSchedTime(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3926" data-mp="18c9fphq2nv79" />
              {durationMsg && <p className={`text-[12px] font-bold mt-2 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3927" data-mp="2v4fsqyq9o2e">
                  {durationMsg}
                </p>}
              {rangeInvalid && <div className="flex items-center gap-1.5 mt-2 text-[#EF4444]" data-id="element-3928" data-mp="cm8tlnco32vn">
                  <AlertTriangleIcon className="w-4 h-4" data-id="element-3929" data-mp="3kzy0d3mf4l4i" />
                  <p className="text-[11px] font-medium" data-id="element-3930" data-mp="3l2kexn7q80w8">
                    {schedFreq === 'Custom' && !schedCustomDays ? 'Enter the number of days between payments' : 'End Date must be on or after Start Date'}
                  </p>
                </div>}
            </div>}

          <div data-id="element-3931" data-mp="1a7n0elrzpg20">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3932" data-mp="1a7dcr4uf3kzh">
              Amount
            </label>
            <input type="text" inputMode="numeric" value={schedAmount} onChange={e => setSchedAmount(e.target.value.replace(/[^\d]/g, ''))} placeholder="Enter Amount" className={`w-full h-[54px] px-4 rounded-xl text-[18px] font-bold outline-none border-2 transition-colors ${textPri} ${schedAmount !== '' && amountNum > CUSTOMER.balance ? 'border-[#EF4444]' : 'border-transparent focus:border-[#F37024]'} ${cardSurface}`} data-id="element-3933" data-mp="1ctdywj2x4pgf" />
            {schedAmount !== '' && amountNum > CUSTOMER.balance && <div className="flex items-center gap-1.5 mt-1.5 text-[#EF4444]" data-id="element-3934" data-mp="1ms3gsbute9wo">
                <AlertTriangleIcon className="w-4 h-4" data-id="element-3935" data-mp="1qsng9m2v1plz" />
                <p className="text-[11px] font-medium" data-id="element-3936" data-mp="1qr2m3xkgk8on">
                  Amount exceeds transaction limit
                </p>
              </div>}
            <div className="flex gap-2 mt-3" data-id="element-3937" data-mp="2j9g7uysis47d">
              {['200', '500', '1000', '5000'].map(amt => <button key={amt} onClick={() => setSchedAmount(amt)} className={`flex-1 py-2 rounded-lg text-[12px] font-bold transition-colors ${schedAmount === amt ? 'bg-[#F37024] text-white' : `${cardSurface} ${textSec}`}`} data-id="element-3938" data-mp="vlwg78jjif1v">
                  {amt}
                </button>)}
            </div>
          </div>

          {isRecurring && schedAmount !== '' && amountNum > 0 && <div className={`p-4 rounded-xl ${cardSurface}`} data-id="element-3939" data-mp="19cjj7cwc2r3r">
              <div className="flex items-center justify-between" data-id="element-3940" data-mp="19d1jqlq13rhh">
                <span className={`text-[12px] ${textSec}`} data-id="element-3941" data-mp="14khzj5wu5y3p">
                  Amount per Execution
                </span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3942" data-mp="10jg5d3hs0c8l">
                  PKR {formatPKR(amountNum)}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2" data-id="element-3943" data-mp="1savrkwff7wya">
                <span className={`text-[12px] ${textSec}`} data-id="element-3944" data-mp="3axvnoeahl2po">
                  Scheduled Executions
                </span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3945" data-mp="3fwgnwdpqnme4">
                  {executions > 0 ? `× ${executions}` : '—'}
                </span>
              </div>
              <div className={`flex items-end justify-between mt-3 pt-3 border-t ${isLight ? 'border-[#F0E8E3]' : 'border-[rgba(243,112,36,0.15)]'}`} data-id="element-3946" data-mp="3oxn8oyzf7jd6">
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-3947" data-mp="3tunb8zx2a3zj">
                  Total Scheduled Amount
                </span>
                <span className={`text-[18px] font-bold ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3948" data-mp="7gn86138eey4">
                  {executions > 0 ? `PKR ${formatPKR(totalAmount)}` : '—'}
                </span>
              </div>
              {executions === 0 && <p className={`text-[11px] mt-2 ${textSec}`} data-id="element-3949" data-mp="lgetkmw2uxm5">
                  Select a Start and End Date to calculate the total.
                </p>}
            </div>}

          <div data-id="element-3950" data-mp="2cli0dd6456il">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3951" data-mp="2clrhscd3e5co">
              Pay To
            </label>
            <select value={schedPayTo} onChange={e => setSchedPayTo(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface} appearance-none`} data-id="element-3952" data-mp="2rbp8p02ep95k">
              <option value="" disabled data-id="element-3953" data-mp="2wbbjl2uridb7">
                Select Organization
              </option>
              <option value="Al-khidmat Foundation" data-id="element-3954" data-mp="3a6e00jx8jl29">
                Al-khidmat Foundation
              </option>
              <option value="Edhi Foundation" data-id="element-3955" data-mp="1fuh3icjxrp0c">Edhi Foundation</option>
              <option value="Storm Fiber" data-id="element-3956" data-mp="1fwtjwla1prba">Storm Fiber</option>
              <option value="LESCO" data-id="element-3957" data-mp="1awnyvnya7326">LESCO</option>
            </select>
          </div>

          <div data-id="element-3958" data-mp="3mipstsgt1cts">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3959" data-mp="3mkaojleuv1t8">
              Pay From
            </label>
            <select value={schedPayFrom} onChange={e => setSchedPayFrom(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface} appearance-none`} data-id="element-3960" data-mp="3ct68sjhcvcxc">
              <option value="Kashish Shewani / 1234 **** **** 6544" data-id="element-3961" data-mp="38rbc5saddnpw">
                Kashish Shewani / 1234 **** **** 6544
              </option>
              <option value="Platinum Credit Card" data-id="element-3962" data-mp="2tvvc96n3yxll">Platinum Credit Card</option>
            </select>
            <p className={`text-[11px] mt-1.5 ${textSec}`} data-id="element-3963" data-mp="2ytx5uli5ufbb">
              Available Balance: PKR {formatPKR(CUSTOMER.balance)}
            </p>
          </div>

          <div data-id="element-3964" data-mp="x2k425bhonjh">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3965" data-mp="x0z8ccdfuyk1">
              Payment Type
            </label>
            <select value={schedType} onChange={e => setSchedType(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface} appearance-none`} data-id="element-3966" data-mp="3gzvwth0es1g9">
              <option value="" disabled data-id="element-3967" data-mp="2xa3orhh4fmfi">
                Select Reason
              </option>
              <option value="Zakat" data-id="element-3968" data-mp="2x7qzskcy4bsy">Zakat</option>
              <option value="Donation" data-id="element-3969" data-mp="2t7fct0x41zeb">Donation</option>
              <option value="Internet Bill" data-id="element-3970" data-mp="2o9dy0s6pwm8n">Internet Bill</option>
              <option value="Utility Bill" data-id="element-3971" data-mp="2o9noegvocvev">Utility Bill</option>
            </select>
          </div>

          <label className="flex items-start gap-3 cursor-pointer mt-2" data-id="element-3972" data-mp="2jc5d4ju6q43p">
            <div className="mt-0.5" data-id="element-3973" data-mp="mgzhnxjtnpuc">
              <div onClick={() => setSchedTerms(!schedTerms)} className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${schedTerms ? 'bg-[#F37024] border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-3974" data-mp="mfy33j3lkl7v">
                {schedTerms && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-3975" data-mp="yrqgpiowi2p2" />}
              </div>
            </div>
            <p className={`text-[12px] leading-snug ${textSec}`} data-id="element-3976" data-mp="13q1rbvn0sh8c">
              I have read and agreed to the{' '}
              <span className="text-[#F37024] underline" data-id="element-3977" data-mp="65fgi957i3ss">
                Terms and Conditions
              </span>
              .
            </p>
          </label>
        </div>

        <div className="px-6 mt-auto pt-6 flex justify-center" data-id="element-3978" data-mp="g0zaur44guff">
          <button onClick={() => navigateTo('scheduled_review')} disabled={!isValid} style={{
          ...ctaStyle,
          opacity: !isValid ? 0.5 : 1,
          cursor: !isValid ? 'not-allowed' : 'pointer'
        }} className="active:scale-[0.98] transition-transform shadow-md" data-id="element-3979" data-mp="l0lqf77famre">
            {isEditingSched ? 'Update Schedule' : 'NEXT'}
          </button>
        </div>
      </motion.div>;
  };
  // ===== Scheduled Payments: Review =====
  const renderScheduledReview = () => {
    const isRecurring = schedTab === 'recurring';
    const amountNum = Number(schedAmount);
    const {
      executions: occurrences,
      totalAmount
    } = calculateScheduleTotals(amountNum || 0, isRecurring ? schedFreq : 'Once', schedDate, schedEndDate, schedCustomDays);
    return <motion.div key="scheduled_review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3980" data-mp="27a2myf4fgwf7">
        <Header title="Review Schedule" onBackClick={() => navigateTo('scheduled_new', -1)} data-id="element-3981" data-mp="3rsk4pjq2opal" />

        <div className="px-6 mt-2 flex-1 overflow-y-auto space-y-4" data-id="element-3982" data-mp="1g5q8xmnczlk9">
          <div className={`rounded-lg p-5 ${cardSurface}`} data-id="element-3983" data-mp="1c44qzmvywh37">
            <p className={`text-[11px] font-semibold uppercase tracking-wider mb-4 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3984" data-mp="1c1ijfn92u1kz">
              Payment Details
            </p>
            <div className="space-y-3" data-id="element-3985" data-mp="1zswcjvf1y170">
              <div className="flex justify-between" data-id="element-3986" data-mp="1zrv1hqy1depa">
                <span className={`text-[13px] ${textSec}`} data-id="element-3987" data-mp="1zuqwb6el7n9i">
                  Amount per Execution
                </span>
                <span className={`text-[14px] font-bold ${textPri}`} data-id="element-3988" data-mp="28u4agcdnp00k">
                  PKR {formatPKR(amountNum)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3989" data-mp="23vayioe0ph2i">
                <span className={`text-[13px] ${textSec}`} data-id="element-3990" data-mp="eeryv26onux8">
                  Scheduled Executions
                </span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3991" data-mp="9hs0ldg2rwgo">
                  {occurrences}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3992" data-mp="2eht768elo9a5">
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-3993" data-mp="2eicnjkolqpm2">
                  Total Scheduled Amount
                </span>
                <span className={`text-[14px] font-bold ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3994" data-mp="2jja3rrq1x9kx">
                  PKR {formatPKR(totalAmount)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3995" data-mp="2xg5hvyl5tr7u">
                <span className={`text-[13px] ${textSec}`} data-id="element-3996" data-mp="2xelwjtkncpz7">Pay From</span>
                <span className={`text-[13px] font-semibold text-right max-w-[180px] ${textPri}`} data-id="element-3997" data-mp="328q8yscolq7o">
                  {schedPayFrom}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3998" data-mp="3b9o9v1rtphm1">
                <span className={`text-[13px] ${textSec}`} data-id="element-3999" data-mp="3g6n6ps2vg2nx">Pay To</span>
                <span className={`text-[13px] font-semibold text-right max-w-[180px] ${textPri}`} data-id="element-4000" data-mp="3g4tulx9hoq8j">
                  {schedPayTo} / 1234 **** **** 6544
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4001" data-mp="vimuflxido4c">
                <span className={`text-[13px] ${textSec}`} data-id="element-4002" data-mp="vl91lkghm1m1">Payment Type</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4003" data-mp="qlmnz9wqyjcp">
                  {schedType}
                </span>
              </div>
            </div>
          </div>

          <div className={`rounded-lg p-5 ${cardSurface}`} data-id="element-4004" data-mp="1j9s9hyvmkcdl">
            <p className={`text-[11px] font-semibold uppercase tracking-wider mb-4 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-4005" data-mp="1ei0t2h66nn16">
              Schedule Details
            </p>
            <div className="space-y-3" data-id="element-4006" data-mp="26y1r0olt2dyx">
              <div className="flex justify-between" data-id="element-4007" data-mp="26z3czo4d0kvf">
                <span className={`text-[13px] ${textSec}`} data-id="element-4008" data-mp="227v4cuaut3jb">Frequency</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4009" data-mp="225idfrn4biu4">
                  {isRecurring ? schedFreq === 'Custom' ? `${schedCustomDays} Days` : schedFreq : 'Once'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4010" data-mp="1ctqqfr2452yw">
                <span className={`text-[13px] ${textSec}`} data-id="element-4011" data-mp="1hrilgty2157b">
                  {isRecurring ? 'Start Date' : 'Scheduled Date'}
                </span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4012" data-mp="25ifmvcs84k4b">
                  {schedDate}
                </span>
              </div>
              {isRecurring && <div className="flex justify-between" data-id="element-4013" data-mp="1vq04ied0ltiw">
                  <span className={`text-[13px] ${textSec}`} data-id="element-4014" data-mp="20ojoi981sv8m">End Date</span>
                  <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4015" data-mp="2of7c02ug5tga">
                    {schedEndDate}
                  </span>
                </div>}
              <div className="flex justify-between" data-id="element-4016" data-mp="2t7qpkzt4wol3">
                <span className={`text-[13px] ${textSec}`} data-id="element-4017" data-mp="32jebmfz1cqf4">Scheduled Time</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4018" data-mp="2yjdxyxjrgj94">
                  {schedTime}
                </span>
              </div>

            </div>
          </div>

          <div data-id="element-4019" data-mp="37b64ix5nz88f">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-4020" data-mp="37ao03d8ulnpf">
              Comments (Optional)
            </label>
            <textarea value={schedComments} onChange={e => setSchedComments(e.target.value)} placeholder="Add a note..." rows={2} className={`w-full p-3 rounded-xl text-[13px] outline-none border transition-colors resize-none ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-4021" data-mp="2jno9msr3ru18" />
          </div>
        </div>

        <div className="px-6 mt-auto pt-6 flex justify-center" data-id="element-4022" data-mp="8rpj9kmj0io8">
          <button onClick={() => navigateTo('scheduled_mpin')} style={ctaStyle} className="active:scale-[0.98] transition-transform shadow-md" data-id="element-4023" data-mp="3te7v5gl203w">
            CONFIRM
          </button>
        </div>
      </motion.div>;
  };
  // ===== Scheduled Payments: MPIN =====
  const renderScheduledMpin = () => {
    const isComplete = schedMpin.every(d => d !== '');
    return <motion.div key="scheduled_mpin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-4024" data-mp="puei4fue02jw">
        <Header title="Authentication" onBackClick={() => navigateTo('scheduled_review', -1)} data-id="element-4025" data-mp="6w27epntukm7" />
        <div className="px-6 mt-4 flex-1 flex flex-col items-center justify-center" data-id="element-4026" data-mp="35mu8pgjftiqc">
          <div className="w-16 h-16 rounded-full bg-[#F37024]/10 flex items-center justify-center mb-6" data-id="element-4027" data-mp="3akvo9rhnmzx2">
            <LockIcon className="w-8 h-8 text-[#F37024]" strokeWidth={2} data-id="element-4028" data-mp="3325yrkiczzoo" />
          </div>
          <h2 className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-4029" data-mp="2y2t4wft2lscl">
            Enter MPIN
          </h2>
          <p className={`text-[14px] mb-8 text-center ${textSec}`} data-id="element-4030" data-mp="2t2x9h4yfv98n">
            Enter your 4-digit MPIN to confirm this scheduled payment.
          </p>
          <div className="flex gap-3 mb-12" data-id="element-4031" data-mp="2o43xjgysvqal">
            {[0, 1, 2, 3].map(index => <input key={index} id={`sched-mpin-${index}`} type="password" inputMode="numeric" maxLength={1} value={schedMpin[index]} onChange={e => {
            const val = e.target.value.replace(/[^\d]/g, '');
            const next = [...schedMpin];
            next[index] = val;
            setSchedMpin(next);
            if (val && index < 3) document.getElementById(`sched-mpin-${index + 1}`)?.focus();
            if (next.every(d => d !== '')) {
              setTimeout(() => navigateTo('scheduled_success'), 400);
            }
          }} onKeyDown={e => {
            if (e.key === 'Backspace' && !schedMpin[index] && index > 0) {
              document.getElementById(`sched-mpin-${index - 1}`)?.focus();
            }
          }} className={`w-14 h-14 rounded-xl text-center text-xl font-bold outline-none border-2 transition-colors ${schedMpin[index] ? 'border-[#F37024] bg-[#F37024]/10' : cardSurface} ${textPri}`} data-id="element-4032" data-mp="2k4bm8wi0v61j" />)}
          </div>
        </div>
      </motion.div>;
  };
  // ===== Scheduled Payments: Success (canonical receipt) =====
  const renderScheduledSuccess = () => {
    const isRecurring = schedTab === 'recurring';
    const amountNum = Number(schedAmount);
    const {
      executions,
      totalAmount
    } = calculateScheduleTotals(amountNum || 0, isRecurring ? schedFreq : 'Once', schedDate, schedEndDate, schedCustomDays);
    const refNumber = `SCH${Date.now().toString().slice(-9)}`;
    return <TransactionReceipt amount={amountNum} refNumber={refNumber} toName={schedPayTo} toAccount="1234 **** **** 6544" fromName={CUSTOMER.name} fromAccount={CUSTOMER.account} additionalInfo={[{
      label: 'Amount per Execution',
      value: `PKR ${formatPKR(amountNum)}`
    }, {
      label: 'Scheduled Executions',
      value: String(executions)
    }, {
      label: 'Total Scheduled Amount',
      value: `PKR ${formatPKR(totalAmount)}`
    }, {
      label: 'Payment Type',
      value: schedType
    }, {
      label: 'Frequency',
      value: isRecurring ? schedFreq === 'Custom' ? `${schedCustomDays} Days` : schedFreq : 'Once'
    }, {
      label: isRecurring ? 'Start Date' : 'Date & Time',
      value: `${schedDate} ${schedTime}`
    }, ...(isRecurring ? [{
      label: 'End Date',
      value: schedEndDate
    }] : []), {
      label: 'Reference No.',
      value: refNumber
    }]} onNavigateHome={() => navigateTo('scheduled_list')} onShare={handleShareReceipt} onSaveImage={handleDownloadReceipt} data-id="element-4033" data-mp="1z12vqsy8hgsp" />;
  };
  // ===== Scheduled Payments: Detail View =====
  const renderScheduledDetail = () => {
    if (!selectedSched) return null;
    const isRecurring = selectedSched.frequency !== 'Once';
    const {
      totalAmount
    } = calculateScheduleTotals(selectedSched.amount, selectedSched.frequency, selectedSched.startDate, selectedSched.endDate);
    return <motion.div key="scheduled_detail" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-4034" data-mp="m7ea2yb9lbdt">
        <Header title="Schedule Details" onBackClick={() => navigateTo('scheduled_list', -1)} data-id="element-4035" data-mp="14zgk3qtehtf3" />

        <div className="px-6 mt-2 flex-1 overflow-y-auto space-y-4" data-id="element-4036" data-mp="19x8lrcjuktwh">
          <div className="text-center mb-6" data-id="element-4037" data-mp="vysftntt77fe">
            <p className={`text-[13px] ${textSec}`} data-id="element-4038" data-mp="vxqv0rmzr6kh">Scheduled Amount</p>
            <p className={`text-[32px] font-bold mt-1 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-4039" data-mp="zzccyredub1j">
              PKR {formatPKR(selectedSched.amount)}
            </p>
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-[11px] font-bold ${selectedSched.status === 'Active' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`} data-id="element-4040" data-mp="3kql6wrp0a1uf">
              {selectedSched.status}
            </span>
          </div>

          <div className={`rounded-2xl p-5 ${cardSurface}`} data-id="element-4041" data-mp="1hh11dp8n5sjq">
            <div className="space-y-4" data-id="element-4042" data-mp="1hfgaqapg6tr5">
              <div className="flex items-start gap-3" data-id="element-4043" data-mp="1chogvb58sxkb">
                <div className="w-2 h-2 rounded-full bg-[#F37024] mt-1.5 shrink-0" data-id="element-4044" data-mp="1cgvf6ol54y91" />
                <div className="flex-1" data-id="element-4045" data-mp="18f1lj09d4uid">
                  <p className={`text-[11px] uppercase tracking-wider ${textSec}`} data-id="element-4046" data-mp="13hi6hy4ucdpd">
                    Pay From
                  </p>
                  <p className={`text-[14px] font-bold ${textPri}`} data-id="element-4047" data-mp="tjvdk7qpilme">
                    {selectedSched.payFrom}
                  </p>
                  <p className={`text-[12px] ${textSec}`} data-id="element-4048" data-mp="2sx7rlx6z661y">
                    {selectedSched.payFromAccount}
                  </p>
                </div>
              </div>
              <div className="w-px h-6 bg-gray-300 ml-1 -my-2" data-id="element-4049" data-mp="2wf5azjsp3hia" />
              <div className="flex items-start gap-3" data-id="element-4050" data-mp="2wen844bj251e">
                <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 shrink-0" data-id="element-4051" data-mp="30fp66htpkb1s" />
                <div className="flex-1" data-id="element-4052" data-mp="357gx4tgs0isg">
                  <p className={`text-[11px] uppercase tracking-wider ${textSec}`} data-id="element-4053" data-mp="357qsyxoi5k62">
                    Pay To
                  </p>
                  <p className={`text-[14px] font-bold ${textPri}`} data-id="element-4054" data-mp="2dgt27z5p49n8">
                    {selectedSched.payTo}
                  </p>
                  <p className={`text-[12px] ${textSec}`} data-id="element-4055" data-mp="2hi4qo1cu2otu">
                    {selectedSched.payToAccount}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className={`rounded-2xl p-5 ${cardSurface}`} data-id="element-4056" data-mp="2rh3sp133fm7v">
            <div className="space-y-3" data-id="element-4057" data-mp="1r9w6upqwznd">
              <div className="flex justify-between" data-id="element-4058" data-mp="1q8i0hdfnx1f">
                <span className={`text-[13px] ${textSec}`} data-id="element-4059" data-mp="6os9syepk31p">Payment Type</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4060" data-mp="1fm8icxb48qrw">
                  {selectedSched.type}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4061" data-mp="1ap02529j2wta">
                <span className={`text-[13px] ${textSec}`} data-id="element-4062" data-mp="1onpwia4yqidy">Frequency</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4063" data-mp="1oku1atki27t7">
                  {selectedSched.frequency}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4064" data-mp="1yjt969xfxzf5">
                <span className={`text-[13px] ${textSec}`} data-id="element-4065" data-mp="1tmj980cfe5d2">Start Date</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4066" data-mp="1tkgmpqaheud3">
                  {selectedSched.startDate}
                </span>
              </div>
              {isRecurring && selectedSched.endDate && <div className="flex justify-between" data-id="element-4067" data-mp="2hbm1s38llimc">
                  <span className={`text-[13px] ${textSec}`} data-id="element-4068" data-mp="2hcdpkrvyec22">End Date</span>
                  <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4069" data-mp="2cdtykd2i6a2u">
                    {selectedSched.endDate}
                  </span>
                </div>}
              <div className="flex justify-between" data-id="element-4070" data-mp="30y71rucc953j">
                <span className={`text-[13px] ${textSec}`} data-id="element-4071" data-mp="30vkwy2gu13p0">Scheduled Time</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4072" data-mp="2lzv69r0pc2dy">
                  {selectedSched.time}
                </span>
              </div>
              {isRecurring && <div className="flex justify-between" data-id="element-4073" data-mp="2d7b6gn8obrrj">
                  <span className={`text-[13px] ${textSec}`} data-id="element-4074" data-mp="2hyjf3h26j93n">
                    Total Scheduled Amount
                  </span>
                  <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4075" data-mp="23aydjrkydhxu">
                    PKR {formatPKR(totalAmount)}
                  </span>
                </div>}
              <div className="flex justify-between" data-id="element-4076" data-mp="1u8p6jbow86p7">
                <span className={`text-[13px] ${textSec}`} data-id="element-4077" data-mp="1ubbkpu6ahkge">Created On</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4078" data-mp="1yaanb84jt54o">
                  {selectedSched.createdOn}
                </span>
              </div>
            </div>
          </div>

          {/* Collapsible sections mock */}
          <details className={`rounded-2xl ${cardSurface} group`} data-id="element-4079" data-mp="3w4y1deognttg">
            <summary className="p-4 flex justify-between items-center cursor-pointer list-none outline-none" data-id="element-4080" data-mp="3r87muvsb16a5">
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-4081" data-mp="3m6i6d0mbc0eo">
                Past Transactions
              </span>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" data-id="element-4082" data-mp="iruunhyx22ni" />
            </summary>
            <div className="px-4 pb-4 pt-2 border-t border-black/5 dark:border-white/5 space-y-3" data-id="element-4083" data-mp="ds04jgmyth3i">
              <div className="flex justify-between items-center" data-id="element-4084" data-mp="9ry0j0trcbl2">
                <div data-id="element-4085" data-mp="2yj0njul10cnu">
                  <p className={`text-[13px] font-semibold ${textPri}`} data-id="element-4086" data-mp="2yjihulo4ogt4">
                    PKR {formatPKR(selectedSched.amount)}
                  </p>
                  <p className={`text-[11px] ${textSec}`} data-id="element-4087" data-mp="2uji1gvhgxvjf">15/11/2026</p>
                </div>
                <span className="text-[11px] font-bold text-[#22C55E] bg-[#22C55E]/10 px-2 py-1 rounded-full" data-id="element-4088" data-mp="1a17ib2vz45hq">
                  Success
                </span>
              </div>
            </div>
          </details>

          <details className={`rounded-2xl ${cardSurface} group`} data-id="element-4089" data-mp="1iy81mv46rdeb">
            <summary className="p-4 flex justify-between items-center cursor-pointer list-none outline-none" data-id="element-4090" data-mp="1nscbpn8r01jm">
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-4091" data-mp="1spkwlvla6jok">
                Upcoming Transactions
              </span>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" data-id="element-4092" data-mp="21qj1eg3jmv87" />
            </summary>
            <div className="px-4 pb-4 pt-2 border-t border-black/5 dark:border-white/5 space-y-3" data-id="element-4093" data-mp="26pvpf465i8cd">
              <div className="flex justify-between items-center" data-id="element-4094" data-mp="7db4miw0c9jx">
                <div data-id="element-4095" data-mp="cbmdorehag13">
                  <p className={`text-[13px] font-semibold ${textPri}`} data-id="element-4096" data-mp="cb2w5bsqptnl">
                    PKR {formatPKR(selectedSched.amount)}
                  </p>
                  <p className={`text-[11px] ${textSec}`} data-id="element-4097" data-mp="2e71xvibyuthx">
                    {selectedSched.startDate}
                  </p>
                </div>
                <span className="text-[11px] font-bold text-[#F37024] bg-[#F37024]/10 px-2 py-1 rounded-full" data-id="element-4098" data-mp="2nz7nwminr0uh">
                  Pending
                </span>
              </div>
            </div>
          </details>
        </div>

        {selectedSched.status === 'Active' && <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] p-6 bg-gradient-to-t from-black/5 to-transparent flex gap-3" data-id="element-4099" data-mp="1r8yo715q7p4m">
            <button onClick={() => setShowCancelModal(true)} className={`flex-1 py-4 rounded-2xl font-bold text-[14px] border-2 border-[#EF4444] text-[#EF4444] active:scale-[0.98] transition-transform flex items-center justify-center gap-2 ${cardSurface}`} data-id="element-4100" data-mp="25a1drnvxjhka">
              <Trash2Icon className="w-4 h-4" data-id="element-4101" data-mp="3bo0lsoiubirq" />
              Cancel
            </button>
            {!isRecurring && <button onClick={() => {
          setSchedTab('once');
          setSchedDate(selectedSched.startDate);
          setSchedTime(selectedSched.time);
          setSchedAmount(selectedSched.amount.toString());
          setSchedPayTo(selectedSched.payTo);
          setSchedType(selectedSched.type);
          setSchedTerms(true);
          setIsEditingSched(true);
          navigateTo('scheduled_new');
        }} className="flex-1 py-4 bg-[#F37024] text-white font-bold text-[14px] rounded-2xl shadow-md active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-4102" data-mp="xivqu68w4xnr">
                <Edit3Icon className="w-4 h-4" data-id="element-4103" data-mp="203mdv6kkxjnq" />
                Edit
              </button>}
          </div>}

        <AnimatePresence data-id="element-4104" data-mp="24ul2qujmvq2l">
          {showCancelModal && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setShowCancelModal(false)} className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" data-id="element-4105" data-mp="36ulqh0ceq071" />
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[340px] rounded-3xl p-6 z-50 ${cardSurface}`} data-id="element-4106" data-mp="2nw0zd8aimute">
                <div className="w-14 h-14 rounded-full bg-[#EF4444]/10 flex items-center justify-center mx-auto mb-4" data-id="element-4107" data-mp="h6uo9q78vsx2">
                  <AlertTriangleIcon className="w-7 h-7 text-[#EF4444]" data-id="element-4108" data-mp="ma58acvrp6n4" />
                </div>
                <h3 className={`text-[18px] font-bold text-center mb-2 ${textPri}`} data-id="element-4109" data-mp="hix3xv9aobgt">
                  Cancel Schedule?
                </h3>
                <p className={`text-[13px] text-center mb-6 ${textSec}`} data-id="element-4110" data-mp="dfqy2oq3gmav">
                  Are you sure you want to cancel this scheduled payment? This
                  action cannot be undone.
                </p>
                <div className="flex gap-3" data-id="element-4111" data-mp="3upoxn0tbi4ww">
                  <button onClick={() => setShowCancelModal(false)} className={`flex-1 py-3 rounded-xl font-bold text-[14px] border border-gray-300 ${textPri}`} data-id="element-4112" data-mp="3uqgm7rohz2do">
                    Keep Schedule
                  </button>
                  <button onClick={() => {
                setShowCancelModal(false);
                // Mock cancel action
                setScheduledPayments(prev => prev.filter(p => p.id !== selectedSched.id));
                navigateTo('scheduled_list');
              }} className="flex-1 py-3 bg-[#EF4444] text-white font-bold text-[14px] rounded-xl" data-id="element-4113" data-mp="1owgujg4ptzra">
                    Cancel Schedule
                  </button>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>
      </motion.div>;
  };
  const renderMerchantMenu = () => <motion.div key="merchant_menu" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-10 ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-4114" data-mp="3s4r1lsabeyut">
      <Header title="Merchant Payment" onBackClick={() => navigateTo('services', -1)} data-id="element-4115" data-mp="2lqrpogkaadi3" />
      <div className="px-6 mt-2 space-y-4" data-id="element-4116" data-mp="2d6d2eb94p4r3">
        <button onClick={onNavigateToMerchantRequests} className={`w-full p-4 rounded-2xl flex items-center gap-4 text-left transition-colors ${cardSurface}`} data-id="element-4117" data-mp="29747v469l2uh">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{
          background: isLight ? '#FBE4D3' : 'rgba(243,112,36,0.14)'
        }} data-id="element-4118" data-mp="2i7ivia6pau15">
            <FileTextIcon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-4119" data-mp="1pjdb5ojk771u" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-4120" data-mp="247rl0jfe7vrw">
            <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-4121" data-mp="248sysvnsmwdb">
              Payment Request
            </p>
            <p className={`text-[12px] truncate ${textSec}`} data-id="element-4122" data-mp="1br75iwe18ign">
              RTP Now & RTP Later
            </p>
          </div>
          <div className="bg-[#EF4444] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-4123" data-mp="16sm10krqzcme">
            2 Pending
          </div>
          <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-4124" data-mp="a2a9fumq3aqc" />
        </button>
        <button onClick={onNavigateToMerchantHistory} className={`w-full p-4 rounded-2xl flex items-center gap-4 text-left transition-colors ${cardSurface}`} data-id="element-4125" data-mp="j1f8d2275631">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{
          background: isLight ? '#FBE4D3' : 'rgba(243,112,36,0.14)'
        }} data-id="element-4126" data-mp="ntyljxwz1z7b">
            <ClockIcon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-4127" data-mp="12ouz8qnjvers" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-4128" data-mp="16qpzrsxnpo4i">
            <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-4129" data-mp="16popvrsdn7od">
              P2M Transaction History
            </p>
            <p className={`text-[12px] truncate ${textSec}`} data-id="element-4130" data-mp="34iu68ztn39yo">
              View history and request refunds
            </p>
          </div>
          <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-4131" data-mp="30zd85r1wvvio" />
        </button>
      </div>
    </motion.div>;
  return <div className={`w-full min-h-screen overflow-hidden ${pageBg}`} data-id="element-4132" data-mp="2r8r2aojzyjd6">
      <AnimatePresence mode="wait" custom={direction} data-id="element-4133" data-mp="2r6xuh5xndt3l">
        {step === 'services' && renderServices()}
        {step === 'orgs' && renderOrgs()}
        {step === 'review' && renderReview()}
        {step === 'otp' && renderOtp()}
        {step === 'success' && renderSuccess()}
        {step === 'merchant_menu' && renderMerchantMenu()}
        {step === 'scheduled_list' && renderScheduledList()}
        {step === 'scheduled_new' && renderScheduledNew()}
        {step === 'scheduled_review' && renderScheduledReview()}
        {step === 'scheduled_mpin' && renderScheduledMpin()}
        {step === 'scheduled_success' && renderScheduledSuccess()}
        {step === 'scheduled_detail' && renderScheduledDetail()}
      </AnimatePresence>
    </div>;
}